import { Check } from '@sparkware/app-utils';
import { observable, action, computed } from 'mobx';
import { ServiceBase } from './../ServiceBase';
import { IVideoLiveStreamPlayer } from './video-live-stream-players/video-live-stream-player.interface';
import { IStreamData, IStreamInfo } from './video-live-stream.service.interface';
import { IServiceFactoryExtended } from './../service-factory-extended.interface';
import { IVideoLiveStreamService } from './video-live-stream.service.interface';

const TEST_DATA = {
    2330093: {
        streamSource: 'https://888-promo.cdn.ptlive.video/promo-888-abw.smil/playlist.m3u8',
        watchTimeLimit: 120000
    },
    2330017: {
        streamSource: 'https://888-promo.cdn.ptlive.video/promo-888-qr.smil/playlist.m3u8',
        watchTimeLimit: 120000
    },
    2330030: {
        streamSource: 'https://888-promo.cdn.ptlive.video/promo-888-agr.smil/playlist.m3u8',
        watchTimeLimit: 120000
    },
    2330162: {
        streamSource: 'https://888-promo.cdn.ptlive.video/promo-888-mfr.smil/playlist.m3u8',
        watchTimeLimit: 120000
    },
    2010163: {
        streamSource: 'https://live105.egprom.com/app/43/amlst:dc3_ct_auto/playlist.m3u8',
        watchTimeLimit: 120000
    },
    2010033: {
        streamSource: 'https://live105.egprom.com/app/10/amlst:lightr1_imr_auto/playlist.m3u8',
        watchTimeLimit: 120000
    },
    2010013: {
        streamSource: 'https://live105.egprom.com/app/10/amlst:888r1_imr_auto/playlist.m3u8',
        watchTimeLimit: 120000
    },
    2010015: {
        streamSource: 'https://live105.egprom.com/app/10/amlst:888nlr1_imr_auto/playlist.m3u8',
        watchTimeLimit: 120000
    },
    2330187: {
        streamSource: 'https://888-promo.cdn.ptlive.video/promo-888-safari.smil/playlist.m3u8',
        watchTimeLimit: 120000
    },
    2330140: {
        streamSource: 'https://888-promo.cdn.ptlive.video/promo-888-sports-rou.smil/playlist.m3u8',
        watchTimeLimit: 120000
    },
};

export const GA_LIVE_STREAM_CATEGORY = "ORBIT_LIVE_STREAM";

export class VideoLiveStreamService extends ServiceBase implements IVideoLiveStreamService {

    @observable.ref videoStreamsData: IStreamData = {};
    @action.bound
    private _setVideoStreamsData(videoStreamsData: IStreamData): void {
      this.videoStreamsData = videoStreamsData;
    }

    @observable.ref isSupported: boolean = null;
    @action.bound
    private _setIsSupported(isSupported: boolean): void {
      this.isSupported = isSupported;
    }

    @observable.ref isVideoMuted: boolean = true;
    @action.bound
    private _setIsVideoMuted(isVideoMuted: boolean): void {
      this.isVideoMuted = isVideoMuted;
    }

    @observable.ref isAppVisible: boolean = true;
    @action.bound
    private _setIsAppVisible(isAppVisible: boolean): void {
      this.isAppVisible = isAppVisible;
    }

    @computed
    get isDisabledPrelogin(): boolean {
	    if (this.services.user.isAuthorized) {
	        return false;
	    }

        const currentRegulationCode = this.services.regulation.getRegulationCode();
        const disableForRegulationPreLogin = this.services.configuration.servicesConfigs.LiveVideoStream.disableForRegulationPreLogin[currentRegulationCode];
        return !Check.isNullOrUndefined(disableForRegulationPreLogin) && disableForRegulationPreLogin;
    }

    constructor (services: IServiceFactoryExtended) {
        super(services);
        this._init();
        this.services.sdk.events.onAppPaused(() => {
            this._setIsAppVisible(false);
        });
        this.services.sdk.events.onAppResumed(() => {
            this._setIsAppVisible(true);
        });
    }

    private async _init() {
        const isSupported = await this._checkIfSupported();

        if (isSupported) {
            // @TODO: this data should be read from admin. For day 1 launch, it will be hardcoded in the code
            let streamData: IStreamData = {};
            Object.keys(TEST_DATA).forEach((key) => {
                let streamInfo: IStreamInfo = {
                    ...TEST_DATA[key]
                }
                streamData[key] = streamInfo;
            });
            this._setVideoStreamsData(streamData);
        } else {
            this.services.sdk.structuredLoggingService
                .getLogger()
                .addCategory(GA_LIVE_STREAM_CATEGORY)
                .addAction('VideoStreamNotSupported')
                .info();
        }

        this._setIsSupported(isSupported);
    }

    private async _checkIfSupported() {
        const LiveStreamNativePlayer = await import('./video-live-stream-players/native/video-live-stream-player-native').then(module => module.LiveStreamNativePlayer);
        if (LiveStreamNativePlayer.isSupported()) {
            return true;
        }

        const LiveStreamHlsPlayer = await import('./video-live-stream-players/hls/video-live-stream-player-hls').then(module => module.LiveStreamHlsPlayer);
        if (LiveStreamHlsPlayer.isSupported()) {
            return true;
        }

        return false;
    }

    async getLiveStreamPlayer(gameType: number, videoReference: HTMLVideoElement): Promise<IVideoLiveStreamPlayer> {

        const {streamSource, watchTimeLimit} = {...this.videoStreamsData[gameType]}; // use ... destructuring otherwise if this.videoStreamsData[gameType] is undefined it will crash

        const LiveStreamNativePlayer = await import('./video-live-stream-players/native/video-live-stream-player-native').then(module => module.LiveStreamNativePlayer);
        // We need to check also if not android because Evo live stream games not working on android real device because they use WAF and they don't get referer in android native.
        if (LiveStreamNativePlayer.isSupported() && !this.services.device.os.isAndroid) {
            return new LiveStreamNativePlayer(this.services, gameType, videoReference, streamSource, watchTimeLimit);
        }

        const LiveStreamHlsPlayer = await import('./video-live-stream-players/hls/video-live-stream-player-hls').then(module => module.LiveStreamHlsPlayer);
        if (LiveStreamHlsPlayer.isSupported()) {
            return new LiveStreamHlsPlayer(this.services, gameType, videoReference, streamSource, watchTimeLimit);
        }
        return null;
    }

    toggleVideoSound() {
        this._setIsVideoMuted(!this.isVideoMuted);
        this.services.analytics.sendAnalytics(GA_LIVE_STREAM_CATEGORY, "SOUND_CLICK", `${this.isVideoMuted ? "OFF" : "ON"}`, Object.keys(this.videoStreamsData)[0], true);
    }
}
